/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Row, Container, Col, Form } from 'react-bootstrap';
import {Trans, useTranslation} from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styles from './Login.module.css';
import { Credentials } from 'model/Credentials';
import Message from 'containers/common/Message';
import { FloButton, Footer } from 'components';
import { useConfirmationPopupFunction } from 'effects';
import { ConfirmationDialog } from 'containers';
import { MessageType, Message as MessageModel } from 'model';
import User from "model/User";
import { isEmpty } from 'lodash';
import QueryString from 'query-string';

const emailRegEx = /^\S+@\S+\.\S+$/;

type LoginProps = {
  userSignIn: (c: Credentials) => void,
  authenticatedUser: User | undefined,
  isAuthenticated: boolean,
  isSubmitting: boolean,
  clearMessage: () => void,
  showMessage: (message: MessageModel) => void,
  openConfirmationDialog: (metadata?: { [key: string]: any }) => void,
  requestResetPassword: (email: string, locale: string) => void,
}

export const Login: React.FC<LoginProps> = ({userSignIn, isAuthenticated, authenticatedUser, isSubmitting, clearMessage, openConfirmationDialog, requestResetPassword, showMessage}) => {
  const { t, i18n } = useTranslation();

  const [confirmationData, handleOpenConfirmationPopup] = useConfirmationPopupFunction(openConfirmationDialog);
  const history = useHistory();
  const location = useLocation();
  const { from } = location.state as (undefined | { from: any }) || { from: { pathname: "/" } };
  const impersonateAs = QueryString.parse(location.search).impersonate as string | undefined;

  useEffect(() => {
    clearMessage();
  }, []);

  useEffect(() => {
    // authenticatedUser will be set only after a login
    if (isAuthenticated && (!impersonateAs || authenticatedUser)) {
      history.replace(from);
    }
  }, [isAuthenticated, from, history]);

  return (
    <>
      <Container>
        <Row>
          <Col className={styles.logo} >
            <img alt="Flo Multi-Unit Dashboard" src="/images/MoenSmartLogo-md.png" />
          </Col>
        </Row>
        <Row>
          <Col className={styles.title}>
            {t('Manager Dashboard Login')}
          </Col>
        </Row>
        <Row>
          <Col xs="12" md={{ span: 6, offset: 3 }}>
            <Message />
            <Formik
              initialValues={{
                email: '',
                password: ''
              }}
              onSubmit={(values, actions) => {
                userSignIn({
                  username: values.email,
                  password: values.password,
                  impersonateAs: impersonateAs
                });
                actions.setSubmitting(false);
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string().matches(emailRegEx, t('validation_email_invalid')).required(t('validation_required', { field: t('email') })),
                password: Yup.string().required(t('validation_required', { field: t('password') })),
              })}
            >
              {({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit }) => {

                const handleForgotPassword = () => {
                  if ((touched.email && errors.email) || isEmpty(values.email)) {
                    showMessage({ message: t('request-reset-password-email-error'), description: t('request-reset-password-email-description'), type: MessageType.ERROR });
                    return;
                  }
                  clearMessage();
                  handleOpenConfirmationPopup(
                    t('login-password-reset-title'),
                    <Trans i18nKey="login-password-reset-description">This will send password reset instructions to: <strong>{{email: values.email}}</strong></Trans>,
                    t('login-password-reset-action'),
                    () => requestResetPassword(values.email, i18n.language)
                  )
                };

                return (
                  <Form className={styles.form} onSubmit={handleSubmit}>
                    <Form.Group controlId="loginEmail">
                      <Form.Label>{t('Email')}</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder={t('Email')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        isInvalid={!!(touched.email && errors.email)}
                      />
                      {touched.email && errors.email &&
                      <Form.Text className={styles.errorMessage}>{errors.email}</Form.Text>}
                    </Form.Group>
                    <Form.Group controlId="loginPassword">
                      <Form.Label>{t('Password')}</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder={t('Password')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        isInvalid={!!(touched.password && errors.password)}
                      />
                      {touched.password && errors.password &&
                      <Form.Text className={styles.errorMessage}>{errors.password}</Form.Text>}
                    </Form.Group>

                    <FloButton
                      className   = {styles.submitButton}
                      text        = { t('Login') }
                      onClick     = {() => {}}
                      disabled    = {!isValid || isSubmitting}
                      showLoader  = {isSubmitting}
                      fullWidth
                      submit
                    />

                    <div className={styles.forgotPassword} onClick={handleForgotPassword}>
                      {t('Forgot password?')}
                    </div>
                    <ConfirmationDialog
                      className={styles.forgotPasswordConfirmation}
                      title={confirmationData.title}
                      description={confirmationData.description}
                      actionText={confirmationData.actionText}
                      onAccept={confirmationData.onAccept}
                    />
                  </Form>
                );
              }}
            </Formik>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
}

export default Login;
